import { Link } from "react-router-dom";
import { BellRing, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch } from "@/lib/hooks";
import { errorMessage, fmtDate, fmtMoney } from "@/lib/api";

function AlertRow({ a, onDismiss, canDismiss }) {
  return (
    <div className="flex items-start gap-3 border-t border-primary/20 py-3 text-sm first:border-0" data-testid={`tender-alert-${a.id}`}>
      <div className="min-w-0 flex-1">
        <Link to={`/opportunities?open=${a.opportunity_id}`} className="font-medium hover:underline" data-testid={`tender-alert-link-${a.id}`}>{a.title}</Link>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {[a.buyer_name, a.value ? fmtMoney(a.value) : null, a.deadline ? `Deadline ${fmtDate(a.deadline)}` : null, a.campaign_name && `Found by ${a.campaign_name}`].filter(Boolean).join(" · ")}
        </p>
      </div>
      <a href={a.url} target="_blank" rel="noreferrer" className="mt-0.5 text-muted-foreground hover:text-primary" title="Open notice" data-testid={`tender-alert-source-${a.id}`}><ExternalLink className="h-4 w-4" /></a>
      {canDismiss && <button type="button" onClick={() => onDismiss({ id: a.id })} className="mt-0.5 text-muted-foreground hover:text-foreground" title="Dismiss" data-testid={`tender-alert-dismiss-${a.id}`}><X className="h-4 w-4" /></button>}
    </div>
  );
}

export function TenderAlerts() {
  const { api, role } = useWorkspace();
  const { data, reload } = useFetch("/dashboard/alerts");
  if (!data?.total) return null;
  const canDismiss = role !== "read_only";
  const dismiss = async (body) => {
    try { await api.post("/dashboard/alerts/dismiss", body); reload(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="panel mb-6 border-primary/40 bg-primary/5 p-5" data-testid="tender-alerts">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-medium" data-testid="tender-alerts-count">
          <BellRing className="h-4 w-4 text-primary" />{data.total} new confirmed {data.total === 1 ? "tender" : "tenders"} found
        </p>
        {canDismiss && <Button size="sm" variant="ghost" onClick={() => dismiss({ all: true })} data-testid="tender-alerts-dismiss-all">Dismiss all</Button>}
      </div>
      <div className="mt-2">{data.items.map((a) => <AlertRow key={a.id} a={a} onDismiss={dismiss} canDismiss={canDismiss} />)}</div>
    </div>
  );
}
